import React, { useState, useEffect } from 'react';
import { format, addDays, startOfDay, isSameDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

interface AvailabilitySlot {
  start: string;
  end: string;
  available: boolean;
}

interface AvailabilityCalendarProps {
  onSlotSelect: (slot: string) => void;
  selectedSlot?: string;
  disabled?: boolean;
}

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  onSlotSelect,
  selectedSlot,
  disabled = false
}) => {
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(startOfDay(addDays(new Date(), 2)));
  const [clientTimezone, setClientTimezone] = useState<string>('');
  const [hasAutoSelected, setHasAutoSelected] = useState(false);
  
  // Get next 14 days starting from 48 hours from now
  const earliestBookingDate = addDays(new Date(), 2); // 48 hours advance
  const dateRange = Array.from({ length: 14 }, (_, i) => 
    addDays(startOfDay(earliestBookingDate), i)
  );
  
  // Detect client timezone on component mount
  useEffect(() => {
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setClientTimezone(detectedTimezone);
  }, []);
  
  useEffect(() => {
    fetchAvailability();
  }, []);

  // Auto-select first available date and time slot
  useEffect(() => {
    if (availability.length > 0 && !hasAutoSelected && !selectedSlot && !disabled) {
      // Find the first date in dateRange that has available slots (respects 48-hour rule)
      let firstAvailableDate = null;
      let firstAvailableSlot = null;
      
      for (const date of dateRange) {
        const slotsForDate = getSlotsForDate(date);
        const availableSlot = slotsForDate.find(slot => slot.available);
        
        if (availableSlot) {
          firstAvailableDate = date;
          firstAvailableSlot = availableSlot;
          break;
        }
      }
      
      if (firstAvailableDate && firstAvailableSlot) {
        // Set the selected date to the first date with available slots
        setSelectedDate(firstAvailableDate);
        
        // Auto-select the first available time slot for that date
        onSlotSelect(firstAvailableSlot.start);
        setHasAutoSelected(true);
      }
    }
  }, [availability, hasAutoSelected, selectedSlot, disabled, onSlotSelect, dateRange]);
  
  const fetchAvailability = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const startDate = earliestBookingDate;
      const endDate = addDays(startDate, 14);
      
      const response = await fetch(
        `/api/availability?start_date=${startDate.toISOString().split('T')[0]}&end_date=${endDate.toISOString().split('T')[0]}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch availability');
      }
      
      const data = await response.json();
      if (data.success && data.data && data.data.availability) {
        // Flatten the nested structure from the API
        const flattenedSlots: AvailabilitySlot[] = [];
        data.data.availability.forEach((dayData: any) => {
          dayData.slots.forEach((slot: any) => {
            flattenedSlots.push({
              start: slot.start,
              end: slot.end,
              available: slot.available
            });
          });
        });
        setAvailability(flattenedSlots);
      } else {
        throw new Error('Invalid API response format');
      }
      
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError('Unable to load availability. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const getSlotsForDate = (date: Date): AvailabilitySlot[] => {
    return availability.filter(slot => 
      isSameDay(new Date(slot.start), date)
    );
  };
  
  const formatTimeSlot = (dateString: string): string => {
    const date = new Date(dateString);
    if (clientTimezone) {
      // Convert UTC time to client's timezone
      const zonedDate = toZonedTime(date, clientTimezone);
      return format(zonedDate, 'h:mm a');
    }
    return format(date, 'h:mm a');
  };
  
  const getTimezoneAbbreviation = (): string => {
    if (!clientTimezone) return '';
    
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone: clientTimezone
    });
    
    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart ? timeZonePart.value : '';
  };
  
  const isSlotSelected = (slotStart: string): boolean => {
    return selectedSlot === slotStart;
  };
  
  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-brand-green-800 mb-4">
          Available Times
        </h3>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green-600"></div>
          <span className="ml-2 text-brand-stone-600">Loading availability...</span>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-brand-green-800 mb-4">
          Available Times
        </h3>
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchAvailability}
            className="px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h3 className="text-lg font-semibold text-brand-green-800 mb-4">
        Available Times
      </h3>
      
      {/* Date selector */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-brand-stone-700 mb-2">
          Select a date:
        </h4>
        <div className="grid grid-cols-7 gap-2">
          {dateRange.map((date) => {
            const slotsForDate = getSlotsForDate(date);
            const hasSlots = slotsForDate.length > 0;
            const isSelected = isSameDay(date, selectedDate);
            
            return (
              <button
                key={date.toISOString()}
                onClick={() => {
                  setSelectedDate(date);
                  setHasAutoSelected(true); // Prevent re-auto-selection when user manually changes date
                }}
                disabled={!hasSlots || disabled}
                className={`
                  p-2 text-xs rounded-lg border-2 transition-all
                  ${isSelected 
                    ? 'border-brand-green-600 bg-brand-green-50 text-brand-green-800' 
                    : 'border-brand-stone-200 hover:border-brand-green-300'
                  }
                  ${hasSlots && !disabled
                    ? 'cursor-pointer hover:bg-brand-green-50' 
                    : 'cursor-not-allowed opacity-50'
                  }
                `}
              >
                <div className="font-medium">
                  {format(date, 'MMM d')}
                </div>
                <div className="text-xs text-brand-stone-500">
                  {format(date, 'EEE')}
                </div>
                {hasSlots && (
                  <div className="text-xs text-brand-green-600 mt-1">
                    {slotsForDate.length} slots
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Time slots for selected date */}
      <div>
        <h4 className="text-sm font-medium text-brand-stone-700 mb-2">
          Available times for {format(selectedDate, 'EEEE, MMMM d')}:
        </h4>
        
        {(() => {
          const slotsForSelectedDate = getSlotsForDate(selectedDate);
          
          if (slotsForSelectedDate.length === 0) {
            return (
              <p className="text-brand-stone-500 text-sm py-4">
                No available times for this date. Please select another date.
              </p>
            );
          }
          
          return (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {slotsForSelectedDate.map((slot) => (
                <button
                  key={slot.start}
                  onClick={() => {
                    onSlotSelect(slot.start);
                    setHasAutoSelected(true); // Prevent re-auto-selection when user manually selects time
                  }}
                  disabled={disabled}
                  className={`
                    p-3 text-sm rounded-lg border-2 transition-all
                    ${isSlotSelected(slot.start)
                      ? 'border-brand-green-600 bg-brand-green-600 text-white' 
                      : 'border-brand-stone-200 hover:border-brand-green-300 hover:bg-brand-green-50'
                    }
                    ${disabled 
                      ? 'cursor-not-allowed opacity-50' 
                      : 'cursor-pointer'
                    }
                  `}
                >
                  {formatTimeSlot(slot.start)}
                </button>
              ))}
            </div>
          );
        })()}
      </div>
      
      {selectedSlot && (
        <div className="mt-4 p-3 bg-brand-green-50 rounded-lg border border-brand-green-200">
          <p className="text-sm font-medium text-brand-green-800">
            Selected time: {(() => {
              const date = new Date(selectedSlot);
              if (clientTimezone) {
                const zonedDate = toZonedTime(date, clientTimezone);
                return format(zonedDate, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
              }
              return format(date, 'EEEE, MMMM d, yyyy \'at\' h:mm a');
            })()} {getTimezoneAbbreviation()}
          </p>
        </div>
      )}
      
      <div className="mt-4 text-xs text-brand-stone-500">
        <p>• All times are displayed in your local timezone {getTimezoneAbbreviation() && `(${getTimezoneAbbreviation()})`}</p>
        <p>• Sessions are 1 hour long</p>
        <p>• Please arrive 5 minutes early</p>
        {clientTimezone && (
          <p>• Detected timezone: {clientTimezone}</p>
        )}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;